// Calculation logic, presets, currency helpers for the laser cost calculator

export const CURRENCIES = {
  NZD: { code: "NZD", symbol: "$", locale: "en-NZ", label: "NZ Dollar" },
  AUD: { code: "AUD", symbol: "$", locale: "en-AU", label: "AU Dollar" },
  USD: { code: "USD", symbol: "$", locale: "en-US", label: "US Dollar" },
  EUR: { code: "EUR", symbol: "€", locale: "de-DE", label: "Euro" },
  GBP: { code: "GBP", symbol: "£", locale: "en-GB", label: "Pound" },
  CAD: { code: "CAD", symbol: "$", locale: "en-CA", label: "CA Dollar" },
};

export const BREAKDOWN_COLORS = {
  electricity: "#7A8B96",
  flashlamp: "#3E4552",
  major: "#B5704F",
  sundries: "#A38059",
};

// Default calculator parameters (a single laser's full running-cost scenario)
export const DEFAULT_PARAMS = {
  electricityRate: 0.28, // per kWh (clinic-specific)
  powerDraw: 4.0, // kW total input (device rating plate)
  repRate: 2, // Hz — pulses per second while firing
  flashlampCost: 1200, // replacement flashlamp / diode + labour
  flashlampLifespan: 500000, // pulses per lamp
  majorServiceCost: 17000, // repair cost of ONE catastrophic failure (pump chamber, laser head)
  reserveYears: 4, // spread that repair cost across this many years (set-aside horizon)
  annualShots: 5600000, // typical shots fired per year — spreads the reserve onto a per-shot basis
  underWarranty: false, // if currently covered, no reserve needed
  consumablesPerShot: 0, // optional per-shot consumables (most consumables live in the Treatment Builder)
};

// Common devices and their typical rep rate (Hz) — quick presets for the rep-rate field.
export const DEVICE_HZ_PRESETS = [
  { name: "Polylase Alexandrite", repRate: 5 },
  { name: "Polylase Nd:YAG", repRate: 10 },
  { name: "Candela GentleMax Pro", repRate: 2 },
  { name: "Candela GentleMax Pro Plus", repRate: 3 },
  { name: "Lutronic Clarity", repRate: 2 },
  { name: "Lutronic Clarity II", repRate: 3 },
  { name: "Lumenis Splendor X", repRate: 3 },
  { name: "Cynosure Elite iQ", repRate: 2 },
];

// Device-specific spec keys (kept for reference/back-compat).
export const DEVICE_SPEC_KEYS = [
  "powerDraw",
  "repRate",
  "flashlampCost",
  "flashlampLifespan",
  "majorServiceCost",
];

// No built-in presets — operators save their own lasers, each with its full calculation.
export const PRESET_DEVICES = [];

// Quick treatment presets (pulses per session)
export const TREATMENT_PRESETS = [
  { name: "Lip & Chin", pulses: 80 },
  { name: "Underarms", pulses: 200 },
  { name: "Bikini Line", pulses: 450 },
  { name: "Full Legs", pulses: 3500 },
  { name: "Full Body", pulses: 8000 },
];

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : Math.max(0, n);
};

export function calculate(p) {
  const electricityRate = num(p.electricityRate);
  const powerDraw = num(p.powerDraw);
  const repRate = num(p.repRate);
  const flashlampCost = num(p.flashlampCost);
  const flashlampLifespan = num(p.flashlampLifespan);
  const majorServiceCost = num(p.majorServiceCost);
  const reserveYears = num(p.reserveYears) || num(p.failuresPerYear); // back-compat with old saved devices
  const underWarranty = !!p.underWarranty;
  const consumablesPerShot = num(p.consumablesPerShot);
  const annualShots = num(p.annualShots);

  // Electricity is TIME-based: machine kW × electricity price = cost per hour of use.
  // (No reliable per-pulse figure — we only have kW and price.)
  const elecPerHour = powerDraw * electricityRate;
  // ...but for a shot count we can convert via the rep rate: shots ÷ (Hz × 3600) = firing hours.
  const shotsPerHour = repRate * 3600;
  const elecPerShot = shotsPerHour > 0 ? elecPerHour / shotsPerHour : 0;

  // Flashlamp (regular servicing) is genuinely per-shot: it wears out per pulse.
  const flashPerPulse = flashlampLifespan > 0 ? flashlampCost / flashlampLifespan : 0;

  // Major failure reserve: a TIME-based set-aside — one repair cost spread across N years.
  const annualReserve = underWarranty || reserveYears <= 0 ? 0 : majorServiceCost / reserveYears;
  const monthlyReserve = annualReserve / 12;
  // Reserve on a per-shot basis = yearly set-aside ÷ typical shots fired per year.
  const reservePerShot = annualShots > 0 ? annualReserve / annualShots : 0;

  // Per-shot cost (flashlamp + optional consumables) — the "flashlamp cost per shot" hero figure.
  const totalPerPulse = flashPerPulse + consumablesPerShot;
  // Fully-loaded per shot: flashlamp servicing + consumables + electricity + reserve.
  const fullyLoadedPerPulse = flashPerPulse + consumablesPerShot + elecPerShot + reservePerShot;
  // Machine-only per pulse (ex consumables) — used by the Treatment Builder for the per-shot component.
  const totalPerPulseExSundries = flashPerPulse;

  return {
    elecPerHour,
    elecPerShot,
    flashPerPulse,
    consumablesPerPulse: consumablesPerShot,
    reservePerShot,
    totalPerPulse,
    fullyLoadedPerPulse,
    totalPerPulseExSundries,
    annualReserve,
    monthlyReserve,
    // per-pulse breakdown (for chart) — electricity is time-based, shown separately
    breakdownPerPulse: {
      flashlamp: flashPerPulse,
      consumables: consumablesPerShot,
    },
  };
}

// Fully-loaded machine running cost for a given number of shots
// (flashlamp servicing + consumables + electricity + major-failure reserve).
export function costForShots(result, shots) {
  return (result.fullyLoadedPerPulse || 0) * (parseFloat(shots) || 0);
}

// Component breakdown of the cost for a shot count — for display in the lookup card.
export function costForShotsBreakdown(result, shots) {
  const s = parseFloat(shots) || 0;
  const flashlamp = (result.flashPerPulse || 0) * s;
  const consumables = (result.consumablesPerPulse || 0) * s;
  const electricity = (result.elecPerShot || 0) * s;
  const reserve = (result.reservePerShot || 0) * s;
  return { flashlamp, consumables, electricity, reserve, total: flashlamp + consumables + electricity + reserve };
}

export function fmtCurrency(value, currency, decimals = 2) {
  const c = CURRENCIES[currency] || CURRENCIES.USD;
  try {
    return new Intl.NumberFormat(c.locale, {
      style: "currency",
      currency: c.code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value || 0);
  } catch {
    return `${c.symbol}${(value || 0).toFixed(decimals)}`;
  }
}

// Small-value formatter for per-pulse figures (needs more precision)
export function fmtPerPulse(value, currency) {
  const c = CURRENCIES[currency] || CURRENCIES.USD;
  const v = value || 0;
  const decimals = v > 0 && v < 0.01 ? 5 : 4;
  try {
    return new Intl.NumberFormat(c.locale, {
      style: "currency",
      currency: c.code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(v);
  } catch {
    return `${c.symbol}${v.toFixed(decimals)}`;
  }
}

export function fmtNumber(value) {
  return new Intl.NumberFormat("en-US").format(Math.round(value || 0));
}
